/**
 * 投资域内核（第 19 域 `S`）—— **加权成本 / 卖出拆腿 / 三个回报率** 的唯一实现处
 *
 * 依据：`DR-059`（D2 移动加权 · D3 拆腿计入收支 · D4 三档回报率 + 年化）·
 * `投资域PRD_持仓与买卖` **v2** §6（口径细则）· 规格 §22（定点与精度）·
 * `DR-004`（不跨币相加）· 铁律 24（防双实现）· 铁律 8（红涨绿跌）
 *
 * ## 🔴 这个文件只有四件事，且每件只写一次
 *
 * | 函数 | 唯一负责 | 谁在用 |
 * |---|---|---|
 * | `holdingMetrics()` | 成本 / 市值 / 浮盈 / 浮盈率 | 概览接口、持仓行 |
 * | `weightedCostPrice()` | 买入后的移动加权成本价 | `POST /trades`（buy） |
 * | `sellBreakdown()` + `buildSellLegs()` | 卖出成本/回款/盈亏，以及**腿怎么拆** | `POST /trades`（sell） |
 * | `composeInvestmentReturn()` | 三个回报率 + 闭合差 | 概览接口 |
 *
 * ## 🔴 三条容易写错的地方（都写成断言/注释了，改前必读）
 *
 * 1. **买入的加权成本用"总成本 ÷ 总数量"，不是"两个成本价取平均"** ——
 *    后者在两次买入数量不同时会偏。（`AC-INV5` 专测这条）
 * 2. **卖出拆腿后两条腿之和必须等于回款**，否则账户余额与回款脱节（`AC-INV10`）。
 * 3. **`currentPrice/costPrice` 为 0 时收益率返回 `null` 而不是 0** ——
 *    "算不出来"与"收益为零"是两件事（`AC-INV7` 的已清仓行靠这条）。
 */

import { divideExact, priceToCents, ratioE4, toCents } from './money.js';
import type { Cents, PriceE4, QtyE4 } from '../types/brand.js';

// ─────────────────────────── 品种 ───────────────────────────

/**
 * 持仓品种（6 类）—— 🔴 **只决定界面文案与输入单位，不产生第二套算法**。
 *
 * 中文名**不在本文件**：铁律 103（分类中文名不许出现在 shared/server 字面量），
 * 端侧 `ui/HoldingsList.tsx` 的 `KIND_LABEL` 是唯一处。
 */
export const HoldingKind = {
  Stock: 'stock',
  Fund: 'fund',
  Bond: 'bond',
  Wealth: 'wealth',
  Deposit: 'deposit',
  Other: 'other',
} as const;
export type HoldingKind = (typeof HoldingKind)[keyof typeof HoldingKind];

/** 契约/DB 允许的全部取值（顺序即界面顺序） */
export const HoldingKindValues = [
  HoldingKind.Stock,
  HoldingKind.Fund,
  HoldingKind.Bond,
  HoldingKind.Wealth,
  HoldingKind.Deposit,
  HoldingKind.Other,
] as const;

/**
 * **金额制**品种（`DR-059` §5）：买入/赎回时用户填的是"多少钱"，不是"多少份"。
 *
 * 🔴 底层仍落 `(quantityE4, priceE4)`：端侧用 `divideExact(金额分, …)` 折份额，
 * 服务端只校验份额（`AC-INV11` 管的是"腿金额必须等于实际到账"）。
 */
export const AMOUNT_UNIT_KINDS: readonly HoldingKind[] = [HoldingKind.Wealth, HoldingKind.Deposit];

/** 有到期概念的品种（`maturity_date` 只对这些有意义） */
export const MATURITY_KINDS: readonly HoldingKind[] = [
  HoldingKind.Wealth,
  HoldingKind.Deposit,
  HoldingKind.Bond,
];

// ─────────────────────────── 金额 ⇄ 份额 ───────────────────────────

/**
 * **金额制品种的「金额 → 份额」换算**（`投资域PRD` §5 · `AC-INV11`）。
 *
 * 理财 / 存款用户填的是"多少钱"，底层要落 `(quantityE4, priceE4)` 一对数 ⇒ 必需这一步。
 *
 * ```
 * q(份) = A(元) ÷ P(元/份)  ⇒  q_E4 = A_分 × 1e6 ÷ P_E4
 * ```
 *
 * ## 🔴 为什么返回份额而不是让调用方自己除
 *
 * `1e6` 这个常数推导错一次就差 100 倍，而且**错得很安静**（金额看起来还在合理量级）。
 * 全项目只有这一处写这个除法（铁律 24），端侧与测试都从这取。
 *
 * ## ⚠️ 除不尽是**正常**的，不是错误
 *
 * 用户填 ¥1,000、现价 ¥1.03 ⇒ 份额 970.8738（取整到万分之一份），
 * 回款 `priceToCents(970.8738, 1.03)` = **¥999.99** ⇒ 差的那 1 分必须回显给用户
 * （`AC-INV11` 原文："资金腿金额 = 实际到账，≠ 1000 时不许写成 1000"）。
 * 本函数**只给份额**，实际到账由调用方用 {@link priceToCents} 反算。
 *
 * @returns 份额（×10⁴），四舍五入到万分之一份；`amountCents ≤ 0` 或 `priceE4 ≤ 0` ⇒ `0`
 */
export function amountToQtyE4(amountCents: number, priceE4: number): QtyE4 {
  if (!Number.isInteger(amountCents) || amountCents <= 0) return 0 as QtyE4;
  if (!Number.isInteger(priceE4) || priceE4 <= 0) return 0 as QtyE4;
  return divideExact(amountCents * 1_000_000, priceE4) as QtyE4;
}

/**
 * **银行口径的「金额 → 份额」**：折算出份额后**四舍五入到 `0.01` 份**（🆕 `DR-072` `D1`）。
 *
 * ## 🔴 与 {@link amountToQtyE4} 的唯一差别，以及为什么必须有一个新函数
 *
 * `amountToQtyE4` 取整到**万分之一份**（`970.8738`），而银行确认单上的份额只到
 * **百分之一份**（`970.87`）。用前者给用户填表，赎回时永远差那 `0.0038` 份 ——
 * 持仓清不干净、份额与银行对不上。
 *
 * ⚠️ **是四舍五入，不是截断**（铁律 47）：`divideExact(x, 100)` 本身带舍入，
 * 再乘回 100 即得 `0.01` 的整数倍。别在这里写 `Math.trunc` 之类的截断 ——
 * 截断会让"按金额买入"永远少买一点，误差单向累积。
 *
 * @returns 份额（×10⁴，恒为 `100` 的整数倍）；入参非法 ⇒ `0`
 */
export function amountToQty2E4(amountCents: number, priceE4: number): QtyE4 {
  const raw = amountToQtyE4(amountCents, priceE4) as number;
  if (raw <= 0) return 0 as QtyE4;
  return (divideExact(raw, 100) * 100) as QtyE4;
}

/**
 * **反算净值** = 成交金额 ÷ 份额（🆕 `DR-072` `D6` · 界面「总价 + 份额 ⇒ 净值」这一路）。
 *
 * ## 🔴 这是三条联动里**最不可靠**的一条，必须提示用户
 *
 * 净值粒度 `0.0001` 元/份 ⇒ 舍入误差 `0.00005 × 份额`：
 * 一万份就是 **0.5 元**（份额越大越离谱）。所以界面必须标明
 * 「净值由金额反算」—— 让用户知道它不是银行公布的那个数。
 *
 * @returns 净值（×10⁴）；入参非法 ⇒ `0`
 */
export function amountToPriceE4(amountCents: number, quantityE4: number): PriceE4 {
  if (!Number.isInteger(amountCents) || amountCents <= 0) return 0 as PriceE4;
  if (!Number.isInteger(quantityE4) || quantityE4 <= 0) return 0 as PriceE4;
  return divideExact(amountCents * 1_000_000, quantityE4) as PriceE4;
}

// ─────────────────── 三数容差（`DR-072` D4 / `AC-INV19`） ───────────────────

/**
 * 成交金额与「份额 × 净值」的**最小**容差（分）。
 *
 * 为什么是 2：份额 `0.01` × 净值 `0.0001` ⇒ 乘积舍入 ≤ 半分；银行自己也四舍五入到分
 * ⇒ 正常情形 ≤ 1 分，留 1 分人工余量。真实业务（场外基金 / 理财，净值 1~3 元）都在这条线内。
 */
export const GROSS_TOLERANCE_CENTS = 2;

/**
 * 按「份额粒度 + 净值粒度」放大的容差（分）—— `DR-072` `D4`。
 *
 * ## 🔴 为什么要两项，缺一项就会把**正确数据**判成"三个数对不上"
 *
 * 银行确认单上的三个数各按自己的精度给，而我们只存 4 位净值、2 位份额
 * ⇒ 乘积与银行金额之间**必然**存在由"我们填不下的位数"造成的差，两个来源相加：
 *
 * | 来源 | 上限（元） | 换成"分" |
 * |---|---|---|
 * | 份额被限定到 `0.01`（半个最小单位） | `净值 × 0.005` | `priceE4 ÷ 20_000` |
 * | 净值只存到 `0.0001`（银行常给 5~6 位，半个最小单位） | `份额 × 0.00005` | `qtyE4 ÷ 2_000_000` |
 *
 * ## 🔴 实测反例（批 `126` 的起因，2026-10-31 生产真账）
 *
 * 赎回 49451.09 份、银行净值 1.015460（我们只能填 1.0155）、银行确认 ¥50,215.63，
 * 我们算出 ¥50,217.58 —— **差 ¥1.95**。而第一版公式只算了份额项（2 分），
 * 于是这条**完全正确**的数据被 `400` 拦住，用户只好另记一笔 ¥1.95「手续费」去平账。
 *
 * 净值项在这里是 `494510900 ÷ 2_000_000 ≈ 247 分`，加上份额项 1 分 + 1 分余量 = **249 分**，
 * ¥1.95 顺利通过。
 *
 * ## ⚠️ 它只是"物理下限"，不是宽容
 *
 * 这是「4 位净值 + 2 位份额」在数学上**可能**产生的最大差。超出它就一定是**录错了**
 * （串了产品、把手续费混进来、少打一位），该拦。
 *
 * @param priceE4 净值（万分之一元/份）；非法按 0 计
 * @param quantityE4 份额（万分之一份）—— 🔴 **必填**：批 `126` 的 bug 正是"漏了它"，
 *   所以这里**不给默认值**，让漏传在编译期就炸（铁律：把口头纪律变成编译错误）
 */
export function grossToleranceCents(priceE4: number, quantityE4: number): number {
  const price = Number.isInteger(priceE4) && priceE4 > 0 ? priceE4 : 0;
  const qty = Number.isInteger(quantityE4) && quantityE4 > 0 ? quantityE4 : 0;
  // 两项合起来只有一次取整：5e-7 × (100 × 净值 + 份额)  ⇒ 分母 2_000_000
  const bound = divideExact(100 * price + qty, 2_000_000) as number;
  const sum = bound + 1;
  return sum > GROSS_TOLERANCE_CENTS ? sum : GROSS_TOLERANCE_CENTS;
}

// ─────────────────────────── 持仓估值 ───────────────────────────

/** 持仓的三元组（数量 ×10⁴ / 成本价与现价 万分之一元每份） */
export interface HoldingValuation {
  quantityE4: number;
  costPriceE4: number;
  currentPriceE4: number;
}

/** 单条持仓的四个派生数 */
export interface HoldingMetrics {
  /** 剩余持仓成本（整数分） */
  costCents: Cents;
  /** 当前市值（整数分） */
  marketValueCents: Cents;
  /** 浮盈 = 市值 − 成本（可为负） */
  unrealizedCents: Cents;
  /** 浮盈率（万分之一）；🔴 `costCents === 0` ⇒ `null`（不是 0%） */
  unrealizedReturnE4: number | null;
}

/**
 * 成本 / 市值 / 浮盈 / 浮盈率。
 *
 * 🔴 `unrealizedReturnE4 = 浮盈 ÷ 成本`，等价于 `(现价 − 成本价) ÷ 成本价`，
 * 但用**金额**相除可避免"成本价四舍五入导致百分比与金额不一致"。
 */
export function holdingMetrics(v: HoldingValuation): HoldingMetrics {
  const costCents = priceToCents(toQty(v.quantityE4), toPrice(v.costPriceE4));
  const marketValueCents = priceToCents(toQty(v.quantityE4), toPrice(v.currentPriceE4));
  const unrealizedCents = toCents((marketValueCents as number) - (costCents as number));
  const unrealizedReturnE4 =
    costCents === 0 ? null : ratioE4(unrealizedCents, costCents);
  return { costCents, marketValueCents, unrealizedCents, unrealizedReturnE4 };
}

function toQty(n: number): QtyE4 {
  return n as QtyE4;
}
function toPrice(n: number): PriceE4 {
  return n as PriceE4;
}

// ─────────────────────────── 买入：移动加权 ───────────────────────────

/**
 * 买入后的**移动加权成本价**（`AC-INV5`）。
 *
 * ```
 * 新成本价 = (原有成本金额 + 本次买入金额) ÷ 新数量        // 取整到万分之一元/份
 * ```
 *
 * ## 🔴 为什么是"金额相加"而不是"成本价取平均"
 *
 * 两次买入 `100 份 @1 元` + `900 份 @2 元`：
 * - 金额相加：(100 + 1800) ÷ 1000 = **1.90** ✓
 * - 价格取平均：(1 + 2) ÷ 2 = 1.50 ✗（差了 26%）
 *
 * ## 单位换算推导（别自己再推一遍，容易错在 `1e6`）
 *
 * 数量 `q`（×10⁴ 份）、成本价 `c`（万分之一元/份）⇒ 金额分 = `q × c ÷ 1e6`。
 * 反解成本价：`c = 金额分 × 1e6 ÷ q`（取整）。
 *
 * @returns 新的成本价（万分之一元/份）；`q0 + q1 ≤ 0` 时返回原成本价（无意义情形不猜）
 */
export function weightedCostPrice(
  q0: number,
  costPrice0: number,
  q1: number,
  price1: number,
  /**
   * 本次买入的**成交金额**（整数分）—— 🆕 `DR-072` `D2`。
   *
   * 给了就用它，而不是 `priceToCents(q1, price1)`：银行的实付金额是权威，
   * 用乘积会让成本里少/多那一分（一万份时更高），而"成本"正是算盈亏的基数。
   */
  addCostCents?: number | null,
): PriceE4 {
  const totalQty = q0 + q1;
  if (totalQty <= 0) return toPrice(costPrice0);

  const oldCost = priceToCents(toQty(q0), toPrice(costPrice0)) as number;
  const addCost =
    addCostCents === undefined || addCostCents === null
      ? (priceToCents(toQty(q1), toPrice(price1)) as number)
      : addCostCents;
  const totalCost = oldCost + addCost;

  return divideExact(totalCost * 1_000_000, totalQty) as PriceE4;
}

// ─────────────────── 撤销：按流水重放（`DR-073` · 批 127） ───────────────────

/**
 * 重放用的最小事实（只取与持仓数量 / 成本价有关的字段）。
 *
 * 🔴 **顺序由调用方决定**（按 `date` 再按 `created_at` 升序）—— 内核不排序：
 * 它拿不到稳定序（同一天多笔只能靠 `created_at`），排序责任在服务层。
 */
export interface ReplayTrade {
  /** `buy` / `sell` / `dividend` / `settle` / … */
  action: string;
  quantityE4: number | null;
  priceE4: number | null;
  /** 成交金额（整数分）；`null` ⇒ 回退 `priceToCents` */
  grossCents?: number | null;
}

/**
 * **按流水重放一个持仓的数量与成本价** —— 撤销交易的唯一实现（批 `127` · `DR-073`）。
 *
 * ## 🔴 为什么必须重放，而不是"反向操作"
 *
 * 撤销一笔买入时，`costPrice` 是**移动加权**的结果 —— 加权不可逆（信息已丢）。
 * 只有把剩下的流水**按原顺序重算一遍**，才能得到与被撤销那笔从未发生完全一致的值：
 *
 * ```
 * qty = 0, cost = 0
 * buy  ⇒ cost = weightedCostPrice(qty_old, cost_old, q, price, gross); qty += q
 * sell ⇒ qty -= q;  qty === 0 ⇒ cost = 0        // 与服务端 sell() 的落库规则逐字一致
 * 其它（dividend / settle）⇒ 不动数量与成本
 * ```
 *
 * ## 为什么"顺序"和"取整"都不会漂
 *
 * 服务端每次买卖都是**就地**用同一个 `weightedCostPrice` 算下一步，本函数按同样顺序、
 * 同样入参再算一遍 ⇒ **逐位相同**（不是"近似还原"）。卖出不改 `costPrice`、清仓才置 0，
 * 这一条也必须照抄，否则清仓过的持仓重放出来成本价会变成非 0。
 *
 * @returns 重放后的 `{ quantityE4, costPriceE4 }`；空数组 ⇒ `{ 0, 0 }`
 */
export function replayHolding(trades: readonly ReplayTrade[]): {
  quantityE4: number;
  costPriceE4: number;
} {
  let quantity = 0;
  let costPrice = 0;

  for (const t of trades) {
    const q = t.quantityE4 ?? 0;
    if (q <= 0) continue;
    const price = t.priceE4 ?? 0;

    if (t.action === 'buy') {
      if (price <= 0) continue; // 缺价的脏行不该把成本算成 0
      costPrice = weightedCostPrice(quantity, costPrice, q, price, t.grossCents ?? null) as number;
      quantity += q;
    } else if (t.action === 'sell') {
      quantity -= q;
      // 🔴 与服务端 sell() 同一条：清仓时成本价归零（数量为 0 时加权成本无意义）
      if (quantity === 0) costPrice = 0;
    }
  }

  return { quantityE4: quantity, costPriceE4: costPrice };
}

// ─────────────────────────── 卖出：拆腿 ───────────────────────────

/** 卖出（赎回）的三数 */
export interface SellBreakdown {
  /** 本次卖出对应的**成本金额**（整数分） */
  costCents: Cents;
  /** 回款（整数分，= 份额 × 卖价） */
  proceedsCents: Cents;
  /** 已实现盈亏 = 回款 − 成本（可正 / 负 / 0） */
  pnlCents: Cents;
}

/** 卖出计算（`AC-INV4`）：成本按**卖出时点**的加权成本价算 */
export function sellBreakdown(
  quantityE4: number,
  costPriceE4: number,
  sellPriceE4: number,
  /**
   * 本次卖出的**成交金额**（整数分）—— 🆕 `DR-072` `D2` / `AC-INV18`。
   *
   * 给了就用它作为回款（`proceedsCents`），而不是 `priceToCents(q, 卖价)`：
   * 银行确认到账是权威，乘积只是它的近似。差额自动进/出盈亏，闭合关系不变。
   */
  grossCents?: number | null,
): SellBreakdown {
  const costCents = priceToCents(toQty(quantityE4), toPrice(costPriceE4));
  const proceedsCents =
    grossCents === undefined || grossCents === null
      ? priceToCents(toQty(quantityE4), toPrice(sellPriceE4))
      : toCents(grossCents);
  return {
    costCents,
    proceedsCents,
    pnlCents: toCents((proceedsCents as number) - (costCents as number)),
  };
}

/** 一条待写资金腿（与 `LegInputSchema` 同形，但金额已是整数分） */
export interface SellLegSpec {
  direction: 'in' | 'out';
  amount: Cents;
  categoryId: string | null;
  excludeFromStats: boolean;
}

/** 盈亏记哪：盈利 → 投资收益（system 收入）；亏损 → 投资理财（system 支出） */
export const INVEST_GAIN_CATEGORY_ID = 'cat-invest-gain';
export const INVEST_LOSS_CATEGORY_ID = 'cat-invest';

/**
 * **卖出拆腿**（`DR-059` D3 · `AC-INV10`）—— 本域最容易被写错的一处。
 *
 * | 情形 | 腿 | 合计 |
 * |---|---|---|
 * | 赚 `R>0` | `in C`（排除）+ `in R`（投资收益） | `C + R = 回款` ✓ |
 * | 亏 `R<0` | `in C`（排除）+ `out \|R\|`（投资理财） | `C − \|R\| = 回款` ✓ |
 * | 平 `R=0` | `in 回款`（排除） | 回款 ✓ |
 *
 * ## 🔴 两种**看似合理但错**的写法
 *
 * - `in 回款`（不排除）⇒ 本金被当收入，报表"投资收益"虚增。
 * - `in C` + `in R` 都进账户、都不排除 ⇒ 账户多进 `R`，**钱凭空出现**。
 *
 * ## 金额为 0 的腿一律剔除
 *
 * 送股/零成本持仓的 `C = 0`；此时只剩盈亏腿，其金额恰等于回款 —— 仍然闭合。
 * （`legs.amount` 必须 > 0，写 0 会被契约层拒绝。）
 */
export function buildSellLegs(b: SellBreakdown): SellLegSpec[] {
  const legs: SellLegSpec[] = [];
  const cost = b.costCents as number;
  const pnl = b.pnlCents as number;

  if (cost > 0) {
    legs.push({ direction: 'in', amount: toCents(cost), categoryId: null, excludeFromStats: true });
  }
  if (pnl > 0) {
    legs.push({
      direction: 'in',
      amount: toCents(pnl),
      categoryId: INVEST_GAIN_CATEGORY_ID,
      excludeFromStats: false,
    });
  } else if (pnl < 0) {
    legs.push({
      direction: 'out',
      amount: toCents(-pnl),
      categoryId: INVEST_LOSS_CATEGORY_ID,
      excludeFromStats: false,
    });
  }

  // 平盘但成本 > 0 时已推入 `in C`；若成本也为 0（回款 0）⇒ 空数组，调用方拒绝
  return legs;
}

// ─────────────────────────── 回报率 ───────────────────────────

/** 一笔投资流水的口径输入（从 `investment_transactions` 取） */
export interface TradeFact {
  /** `buy` / `sell` / `dividend` / `settle` / 其它 */
  action: string;
  quantityE4: number | null;
  priceE4: number | null;
  /** 该行的 `realized_pnl`（分红行 = 分红金额；卖出本 = 盈亏） */
  realizedPnlCents: number | null;
  /**
   * 成交金额（整数分）—— 🆕 `DR-072` `D2`。
   *
   * `null` / 缺省 ⇒ 回退 `priceToCents(q, p)`（历史行即如此，口径与批 125 之前逐分一致）。
   */
  grossCents?: number | null;
}

/**
 * 一组持仓 + 流水的**六个汇总数与三个回报率**。
 *
 * ```
 * B 买入金额   S 卖出回款   D 分红结息   R 已实现盈亏（仅 sell）
 * C 剩余成本   M 当前市值   U 浮盈 = M − C   T 总盈亏 = R + D + U
 * ```
 */
export interface InvestmentReturn {
  investedCents: Cents;
  proceedsCents: Cents;
  dividendCents: Cents;
  realizedPnlCents: Cents;
  holdingCostCents: Cents;
  marketValueCents: Cents;
  unrealizedCents: Cents;
  totalPnlCents: Cents;
  /** 总收益率 = T ÷ B；`B = 0` ⇒ `null` */
  totalReturnE4: number | null;
  /** 已实现收益率 = R ÷ Σ卖出成本；分母 `≤ 0` ⇒ `null` */
  realizedReturnE4: number | null;
  /**
   * 🔴 **闭合差** = `T − (S + D + M − B)`。
   *
   * 新交易必须恒为 `0`（`AC-INV9`）。历史迁移数据实测差 **39 分**（2026-10-24），
   * ⇒ 本函数**如实返回差值**，不做摊平、不强行拉平：把差异藏起来比差 39 分更糟。
   */
  closingDiffCents: Cents;
}

/**
 * 汇总一组（**同币种**）持仓与流水。
 *
 * 🔴 调用方必须先按币种分组再调 —— 本函数**不检查币种**，跨币相加是 `DR-004` 明令禁止的，
 * 这条约束放在服务层（它才知道每行的 `currency`）。
 */
export function composeInvestmentReturn(
  holdings: readonly HoldingValuation[],
  trades: readonly TradeFact[],
): InvestmentReturn {
  let invested = 0;
  let proceeds = 0;
  let dividend = 0;
  let realized = 0;

  for (const t of trades) {
    const q = t.quantityE4 ?? 0;
    const p = t.priceE4 ?? 0;
    // 🔴 成交金额优先（`DR-072` D2）：腿是按它写的，汇总必须同源，否则闭合差会假性非 0
    const amount =
      t.grossCents !== undefined && t.grossCents !== null
        ? t.grossCents
        : q > 0 && p > 0
          ? (priceToCents(toQty(q), toPrice(p)) as number)
          : 0;
    const pnl = t.realizedPnlCents ?? 0;

    if (t.action === 'buy') invested += amount;
    else if (t.action === 'sell') {
      proceeds += amount;
      realized += pnl;
    } else if (t.action === 'dividend' || t.action === 'settle') {
      // 🔴 分红/结息的金额就存在 `realized_pnl` 里（历史数据即如此，见 PRD §1）
      dividend += pnl;
    }
  }

  let holdingCost = 0;
  let marketValue = 0;
  for (const h of holdings) {
    holdingCost += holdingMetrics(h).costCents as number;
    marketValue += holdingMetrics(h).marketValueCents as number;
  }

  const unrealized = marketValue - holdingCost;
  const total = realized + dividend + unrealized;
  const closing = total - (proceeds + dividend + marketValue - invested);

  // 已实现收益率的分母 = Σ卖出成本 = 回款 − 已实现盈亏（由 R = S − C 反解）
  const soldCost = proceeds - realized;

  return {
    investedCents: toCents(invested),
    proceedsCents: toCents(proceeds),
    dividendCents: toCents(dividend),
    realizedPnlCents: toCents(realized),
    holdingCostCents: toCents(holdingCost),
    marketValueCents: toCents(marketValue),
    unrealizedCents: toCents(unrealized),
    totalPnlCents: toCents(total),
    totalReturnE4: invested > 0 ? ratioE4(toCents(total), toCents(invested)) : null,
    realizedReturnE4: soldCost > 0 ? ratioE4(toCents(realized), toCents(soldCost)) : null,
    closingDiffCents: toCents(closing),
  };
}

// ─────────────────────── 报表口径对账（批 99 B3） ───────────────────────

/**
 * 对账的**两组独立事实**（全部为**本币整数分**，且都用正数表达方向）。
 *
 * | 组 | 来源 | 谁算的 |
 * |---|---|---|
 * | `report*` | `legs` 表按分类聚合（`cat-invest-gain` / `cat-invest`） | C 域取数 + `composeIncome` / `composeExpense` |
 * | `domain*` | `investment_transactions.realized_pnl` | 投资域事实表 |
 *
 * 🔴 **这两条路径必须真的独立**：若两边都从同一份聚合结果推出来，比对就是同义反复
 * （铁律 59）—— "永远相等"的检查毫无价值。
 */
export interface ReconcileFacts {
  /** 报表口径：`cat-invest-gain`（收入）的计入收支腿合计 */
  reportGainCents: number;
  /** 报表口径：`cat-invest`（支出）的净支出合计（恒 ≥ 0） */
  reportLossCents: number;
  /** 投资域口径：盈利卖出的已实现盈亏 + 分红 / 结息 */
  domainGainCents: number;
  /** 投资域口径：亏损卖出的已实现亏损（**绝对值**） */
  domainLossCents: number;
}

/** 对账结果 */
export interface ReconcileResult {
  /** 收益侧差额 = 报表 − 投资域（`> 0` 表示报表多出这么多） */
  gainDiffCents: Cents;
  /** 亏损侧差额 = 报表 − 投资域 */
  lossDiffCents: Cents;
  /** 两侧逐分为 0 才算对上 */
  reconciled: boolean;
}

/**
 * **投资收益对账**（`AC-INV9` / `AC-INV4` 的报表侧）—— 只做减法与判零，不做任何聚合。
 *
 * ## 为什么会有差额（历史事实，不是程序错误）
 *
 * v1 迁移只搬了 `investment_transactions`，**没有为它们生成资金腿**（`linked_txn_id` 为空的 23 笔买入即此）。
 * ⇒ 迁移过来的已实现盈亏（实测 ¥804.85）与分红（¥6,588.66）在 `legs` 表里**根本没有对应行**，
 * 报表自然看不到它们。`DR-059` D7 明确：**不补造流水**（补造会凭空多出 23 笔支出）。
 *
 * ⇒ 本函数把差额**如实返回**，由调用方把它与原值一起展示 —— 差额有两种读法，
 * 页面必须说清是哪一种：「历史数据没进报表」而不是「报表算错了」。
 *
 * ## 为什么收益与亏损分开算
 *
 * 盈利进 `cat-invest-gain`（收入分类）、亏损进 `cat-invest`（支出分类）——
 * 若合并成一个净额，盈 100 与亏 100 会抵成 0 而显示"对上了"（`AC-INV4` 的成对纪律）。
 */
export function reconcileInvestment(f: ReconcileFacts): ReconcileResult {
  const gainDiff = f.reportGainCents - f.domainGainCents;
  const lossDiff = f.reportLossCents - f.domainLossCents;
  return {
    gainDiffCents: toCents(gainDiff),
    lossDiffCents: toCents(lossDiff),
    reconciled: gainDiff === 0 && lossDiff === 0,
  };
}
